﻿import { Grid2, TextField, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";

import { FooterDetailsButtons } from "@/controls/buttons/FooterDetailsButtons";
import { NotificationSnackBar } from "@/controls/snackbars/notification-snackbar";
import { PercentField } from "@/shared/components/PercentField";
import { MILL_1 } from "@/setup/mill-config/mill-config";
import { ErrorModal, HelmetDexteel, useUserContext } from "@dexteel/mesf-core";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { SectionFamilySelector } from "../../../../../shared/selectors";
import { Section, SECTION_INITIAL_VALUES } from "../../../models/Section";
import { upsertSection } from "../../../repositories/SectionRepository";
import { SectionGapsGrid } from "./SectionGapsGrid";

type Props = {
	section: Section | null;
	modal: "create" | "update" | "delete" | "";
	onHide: ({
		shouldUpdate,
		close,
		entityId,
	}: {
		shouldUpdate: boolean;
		close: boolean;
		entityId?: number;
	}) => void;
};

export const SectionDetails = ({ section, modal, onHide }: Props) => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const isM1 = Number(defaultAreaId) === MILL_1.id;
	const [message, setNotificationMessage] = useState<string>("");
	const [error, setError] = useState<string>("");
	const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);

	const succesfullyMessage = `The section was ${modal}d succesfully`;

	const {
		control,
		setValue,
		reset,
		handleSubmit,
		getValues,
		formState: { dirtyFields },
	} = useForm<Section>({
		mode: "onChange",
	});

	const isFormDirty = !(Object.keys(dirtyFields).length === 0);

	const queryClient = useQueryClient();
	const upsertMutation = useMutation(upsertSection, {
		onSuccess: async (id: number) => {
			setNotificationMessage(succesfullyMessage);
			await queryClient.invalidateQueries(["sections"]);
			onHide({ shouldUpdate: true, close: false, entityId: id });
		},
		onError: (error: Error) => {
			setError(error.message);
		},
		onSettled: () => setIsSubmitLoading(false),
	});

	const onSubmit: SubmitHandler<Section> = async (data) => {
		setIsSubmitLoading(true);
		if (modal !== "delete") {
			upsertMutation.mutate({ section: data, defaultAreaId });
		}
	};

	useEffect(() => {
		if (modal !== "") {
			reset(section || SECTION_INITIAL_VALUES);
			setIsSubmitLoading(false);
		}
	}, [modal, section]);

	return (
		<>
			{(modal === "create" || modal === "update" || modal === "delete") && (
				<HelmetDexteel
					title={`${
						modal === "create"
							? "New"
							: `${modal === "update" ? "Edit" : "Delete"}`
					} Section - Sections`}
				/>
			)}
			{section === null && modal !== "create" ? (
				<div
					style={{
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						height: "100%",
						width: "100%",
					}}
				>
					<Typography>Select a section or create a new one</Typography>
				</div>
			) : (
				<form id="upsert-section-form" onSubmit={handleSubmit(onSubmit)}>
					<Grid2 container spacing={1} p={1}>
						<Grid2 size={{ xs: 12, sm: 12, md: 2.5 }}>
							<Controller
								name={"SectionName"}
								control={control}
								rules={{ required: "Name is required" }}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										required
										label="Name"
										variant="outlined"
										fullWidth
										size="small"
										value={value}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 2.5 }}>
							<Controller
								name="SectionFamilyId"
								rules={{ required: "Family is required" }}
								control={control}
								render={({ field, fieldState: { error } }) => (
									<SectionFamilySelector
										label="Section Family"
										value={field.value}
										onChange={field.onChange}
										disabled={modal === "delete"}
										fieldError={error}
										required
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 2.5 }}>
							<Controller
								name="TonsPerHour"
								control={control}
								rules={{
									deps: ["TargetTPH"],
									validate: (value) => {
										const targetTPH = getValues("TargetTPH");
										if (!value || !targetTPH) return;

										if (value >= targetTPH) {
											return "Budget TPH cannot be higher than Target TPH";
										}

										return true;
									},
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Budget TPH"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 2.5 }}>
							<Controller
								name="TargetTPH"
								control={control}
								rules={{
									deps: ["TonsPerHour"],
									validate: (value) => {
										const tonsPerHour = getValues("TonsPerHour");
										if (!value || !tonsPerHour) return;

										if (value <= tonsPerHour) {
											return "Target TPH cannot be lower than Budget TPH";
										}

										return true;
									},
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Target TPH"
										type="number"
										variant="outlined"
										fullWidth
										size="small"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						{!isM1 && (
							<Grid2 size={{ xs: 12, md: 2 }}>
								<Controller
									name="LBsPerFoot"
									control={control}
									rules={{
										min: {
											value: 0,
											message: "Must be at least 0",
										},
									}}
									render={({
										field: { onChange, value },
										fieldState: { error },
									}) => (
										<TextField
											label="LBs / Foot"
											type="number"
											variant="outlined"
											fullWidth
											size="small"
											autoComplete="off"
											value={value ?? ""}
											onChange={onChange}
											error={!!error}
											helperText={error?.message}
											InputProps={{
												inputProps: {
													step: "any",
													min: 0,
												},
											}}
										/>
									)}
								/>
							</Grid2>
						)}
						<Grid2 size={{ xs: 12, md: 2 }}>
							<Controller
								name="BilletSize"
								control={control}
								rules={{
									min: {
										value: 1,
										message: "Must be at least 1",
									},
								}}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Billet Size"
										type="number"
										variant="outlined"
										margin="none"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
										InputProps={{
											inputProps: {
												step: 1,
												min: 1,
											},
										}}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 5 }}>
							<Controller
								name="Description"
								control={control}
								render={({
									field: { onChange, value },
									fieldState: { error },
								}) => (
									<TextField
										label="Description"
										variant="outlined"
										margin="none"
										autoComplete="off"
										value={value ?? ""}
										onChange={onChange}
										error={!!error}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 2.5 }}>
							<Controller
								name="MillYield"
								control={control}
								rules={{
									min: {
										value: 0,
										message: "Must be at least 0%",
									},
									max: {
										value: 100,
										message: "Must be less than or equal to 100%",
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<PercentField
										label="Mill Yield"
										value={field.value ?? 0}
										error={!!error}
										onChange={field.onChange}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<Grid2 size={{ xs: 12, md: 2.5 }}>
							<Controller
								name="FinishedYield"
								control={control}
								rules={{
									min: {
										value: 0,
										message: "Must be at least 0%",
									},
									max: {
										value: 100,
										message: "Must be less than or equal to 100%",
									},
								}}
								render={({ field, fieldState: { error } }) => (
									<PercentField
										label="Finished Yield"
										value={field.value ?? 0}
										error={!!error}
										onChange={field.onChange}
										helperText={error?.message}
									/>
								)}
							/>
						</Grid2>
						<SectionGapsGrid section={section!} disabled={modal === "delete"} />
						<FooterDetailsButtons
							isSubmitLoading={isSubmitLoading}
							disabled={!isFormDirty}
							onHide={() => onHide({ shouldUpdate: false, close: true })}
						/>
					</Grid2>
				</form>
			)}
			<NotificationSnackBar
				message={message}
				onHide={() => setNotificationMessage("")}
			/>
			<ErrorModal error={error} onHide={() => setError("")} />
		</>
	);
};
